/**
 * TanStack Query hooks for ingestion data (run history, tables).
 */

import type { Query } from "@tanstack/react-query"
import { useQuery, useQueries, useInfiniteQuery, useQueryClient } from "@tanstack/react-query"
import { RunStatus } from "@/generated/sdk"
import { ingestionApiService, type IngestionRun } from "@/services/ingestionApi"

export const ingestionKeys = {
  all: ["ingestion"] as const,
  runs: (studyId: string) => [...ingestionKeys.all, "runs", studyId] as const,
  runDetail: (studyId: string, runId: string) =>
    [...ingestionKeys.all, "run", studyId, runId] as const,
  runMetrics: (studyId: string) => [...ingestionKeys.all, "runMetrics", studyId] as const,
  tables: (studyId: string) => [...ingestionKeys.all, "tables", studyId] as const,
  tableData: (studyId: string, tableName: string | null | undefined) =>
    [...ingestionKeys.all, "tableData", studyId, tableName ?? null] as const,
  submissionHistory: (studyId: string | undefined, specId: string | undefined) =>
    [...ingestionKeys.all, "submissionHistory", studyId ?? null, specId ?? null] as const,
  dataStreams: (studyId: string | undefined) =>
    [...ingestionKeys.all, "dataStreams", studyId ?? null] as const,
  runsList: (studyId: string | undefined) =>
    [...ingestionKeys.all, "runsList", studyId ?? null] as const,
  quarantine: (studyId: string | undefined, runId: string | undefined) =>
    [...ingestionKeys.all, "quarantine", studyId ?? null, runId ?? null] as const,
  recordTimeline: (studyId: string, tableName: string, primaryKeyValue: string) =>
    [...ingestionKeys.all, "recordTimeline", studyId, tableName, primaryKeyValue] as const,
}

/**
 * Query options for a record's ingestion timeline. Shared by the hook and the prefetch helper so
 * both populate the same cache entry.
 */
function recordTimelineQuery(studyId: string, tableName: string, primaryKeyValue: string) {
  return {
    queryKey: ingestionKeys.recordTimeline(studyId, tableName, primaryKeyValue),
    queryFn: () => ingestionApiService.getRecordTimeline(studyId, tableName, primaryKeyValue),
  }
}

/**
 * Poll cadence for submission history: 5s while any run is still processing (a fast feedback loop
 * during active ingestion), 20s otherwise (background freshness for out-of-band changes).
 */
export function submissionHistoryInterval(runs: IngestionRun[] | undefined): number {
  const hasNonTerminal = runs?.some(
    (r) => r.status === RunStatus.Running || r.status === RunStatus.Pending,
  )
  return hasNonTerminal ? 5000 : 20000
}

/**
 * Fetches paginated run history for a study. Polls every 15s.
 */
export function useRunsList(studyId: string) {
  return useInfiniteQuery({
    queryKey: ingestionKeys.runs(studyId),
    queryFn: ({ pageParam }) =>
      ingestionApiService.listRuns(studyId, { limit: 20, nextToken: pageParam ?? undefined }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextToken ?? null,
    refetchInterval: 15_000,
  })
}

/**
 * Fetch every run for a spec, following nextToken so older submissions are visible.
 */
async function listAllRuns(studyId: string, specId: string): Promise<{ runs: IngestionRun[] }> {
  const runs: IngestionRun[] = []
  let nextToken: string | undefined
  const MAX_PAGES = 50
  for (let page = 0; page < MAX_PAGES; page++) {
    const res = await ingestionApiService.listRuns(studyId, { specId, limit: 20, nextToken })
    runs.push(...(res.runs ?? []))
    nextToken = res.nextToken ?? undefined
    if (!nextToken) break
  }
  return { runs }
}

/**
 * Submission history across every version's ingestion spec.
 */
export function useSubmissionHistory(
  studyId: string | undefined,
  specIds: readonly (string | null | undefined)[],
) {
  const uniqueSpecIds = [...new Set(specIds.filter((id): id is string => Boolean(id)))].sort()
  return useQueries({
    queries: uniqueSpecIds.map((specId) => ({
      queryKey: ingestionKeys.submissionHistory(studyId, specId),
      queryFn: () => listAllRuns(studyId as string, specId),
      enabled: !!studyId,
      refetchInterval: (query: Query<Awaited<ReturnType<typeof listAllRuns>>>) =>
        submissionHistoryInterval(query.state.data?.runs),
    })),
    combine: (results) => {
      const runsById = new Map<string, IngestionRun>()
      for (const result of results) {
        for (const run of result.data?.runs ?? []) {
          runsById.set(run.runId, run)
        }
      }
      const runs = [...runsById.values()].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
      return {
        data: runs,
        isLoading: results.some((result) => result.isLoading),
        refetch: () => Promise.all(results.map((result) => result.refetch())),
      }
    },
  })
}

/**
 * Fetches the (non-paginated) data streams for a study.
 */
export function useDataStreamsList(studyId: string | undefined) {
  return useQuery({
    queryKey: ingestionKeys.dataStreams(studyId),
    queryFn: () => ingestionApiService.listDataStreams(studyId as string),
    enabled: !!studyId,
  })
}

/**
 * Fetches the full run list for a study (single call, used by the upload-history view that joins
 * runs to their data streams client-side).
 */
export function useStudyRunsList(studyId: string | undefined) {
  return useQuery({
    queryKey: ingestionKeys.runsList(studyId),
    queryFn: () => ingestionApiService.listRuns(studyId as string),
    enabled: !!studyId,
  })
}

/**
 * Fetches quarantined records for a run. Disabled until both ids are known.
 */
export function useQuarantineData(studyId: string | undefined, runId: string | undefined) {
  return useQuery({
    queryKey: ingestionKeys.quarantine(studyId, runId),
    queryFn: () => ingestionApiService.getQuarantineData(studyId as string, runId as string),
    enabled: !!studyId && !!runId,
  })
}

/**
 * Fetches a record's ingestion timeline. Disabled until study, table, and primary key are all set.
 */
export function useRecordTimeline(
  studyId: string | undefined,
  tableName: string | undefined,
  primaryKeyValue: string | undefined,
) {
  return useQuery({
    ...recordTimelineQuery(studyId as string, tableName as string, primaryKeyValue as string),
    enabled: !!studyId && !!tableName && !!primaryKeyValue,
  })
}

/**
 * Prefetches a record's ingestion timeline into the cache (e.g. on row hover) so opening it is
 * instant. Returns a bound prefetch function for the given study.
 */
export function usePrefetchRecordTimeline(studyId: string) {
  const queryClient = useQueryClient()
  return (tableName: string, primaryKeyValue: string) => {
    void queryClient.prefetchQuery(recordTimelineQuery(studyId, tableName, primaryKeyValue))
  }
}

/**
 * Fetches run metrics for a study. Polls every 15s (synced with runs list).
 */
export function useRunMetrics(studyId: string) {
  return useQuery({
    queryKey: ingestionKeys.runMetrics(studyId),
    queryFn: () => ingestionApiService.getRunMetrics(studyId),
    refetchInterval: 15_000,
  })
}

/**
 * Prefetches a single run detail into cache (used on row hover).
 */
export function usePrefetchRun(studyId: string) {
  const queryClient = useQueryClient()
  return (runId: string) => {
    void queryClient.prefetchQuery({
      queryKey: ingestionKeys.runDetail(studyId, runId),
      queryFn: () => ingestionApiService.getRun(studyId, runId),
    })
  }
}

/**
 * Fetches available tables for a study.
 */
export function useTablesList(studyId: string) {
  return useQuery({
    queryKey: ingestionKeys.tables(studyId),
    queryFn: () => ingestionApiService.listTables(studyId),
  })
}

/**
 * Fetches data for a specific table. Only runs when tableName is provided.
 */
export function useTableData(studyId: string, tableName: string | null) {
  return useQuery({
    queryKey: ingestionKeys.tableData(studyId, tableName),
    queryFn: () => ingestionApiService.getTableData(studyId, tableName!),
    enabled: !!tableName,
  })
}
